import { View, TouchableOpacity, Text } from 'react-native';
import React, { useState, useCallback, useMemo } from 'react';
import queryString from 'query-string';
import {
  useStaticChartData,
  allValuesZero,
  mapDataToChartFormat,
} from './hook';
import { BarChart, LineChart, PieChart, RadarChart } from './RnCharts';
import { ICharts } from '@nocode/types';

const Charts: React.FC<ICharts> = (props) => {
  const {
    attributes,
    onPress,
    height,
    zIndex,
    dataBinding,
    width,
    opacity,
    componentData,
  } = props;

  const [selectedPeriod, setSelectedPeriod] = useState<'weekly' | 'monthly'>(
    'weekly'
  );
  const [dateOffset, setDateOffset] = useState<number>(0);

  const {
    hasLegend,
    gridLines,
    chartType,
    items,
    backgroundColor,
    textColor,
    // Line Chart specific attributes
    showLegend,
    legendPosition,
    showHorizontalGrid,
    showVerticalGrid,
    gridCount,
    gridLineColor,
    gridLineWidth,
    gridLineStyle,
    maxValue,
    xAxisLabel,
    yAxisLabel,
    xAxisField,
    yAxisField,
    showXAxisLabel,
    showYAxisLabel,
    // Multi Plot specific attributes
    xAxisType,
    xAxisMultiFields,
    xAxisRangeMin,
    xAxisRangeMax,
    xAxisDateField,
    xAxisDateStart,
    xAxisDateEnd,
    yAxisType,
    yAxisMultiFields,
    yAxisRangeMin,
    yAxisRangeMax,
    yAxisDateField,
    yAxisDateStart,
    yAxisDateEnd,
    // Filter button styling
    filterButtonActiveColor,
    filterButtonInactiveColor,
    filterButtonTextActiveColor,
    filterButtonTextInactiveColor,
    showDateFilter,
  } = attributes as any;

  // Navigation functions
  const navigateLeft = useCallback(() => {
    const daysToMove = selectedPeriod === 'weekly' ? -7 : -30;
    setDateOffset((prev) => prev + daysToMove);
  }, [selectedPeriod]);

  const navigateRight = useCallback(() => {
    const daysToMove = selectedPeriod === 'weekly' ? 7 : 30;
    setDateOffset((prev) => prev + daysToMove);
  }, [selectedPeriod]);

  const togglePeriod = useCallback((period: 'weekly' | 'monthly') => {
    setSelectedPeriod(period);
    setDateOffset(0); // Reset to current period when switching
  }, []);

  // Check if x-axis or y-axis are date fields to automatically set grid count to 7
  const xAxisBinding = Array.isArray(xAxisField)
    ? xAxisField.find((item: any) => typeof item === 'object')
    : null;
  const yAxisBinding = Array.isArray(yAxisField)
    ? yAxisField.find((item: any) => typeof item === 'object')
    : null;

  const isXAxisDate =
    xAxisBinding &&
    (xAxisBinding.source?.dataType === 'date' ||
      xAxisBinding.source?.dataType === 'dateOnly' ||
      xAxisBinding.source?.type === 'dateTime');

  const isYAxisDate =
    yAxisBinding &&
    (yAxisBinding.source?.dataType === 'date' ||
      yAxisBinding.source?.dataType === 'dateOnly' ||
      yAxisBinding.source?.type === 'dateTime');

  // Get maxValue from componentData first, then fallback to attributes
  const resolvedMaxValue = componentData?.maxValue
    ? parseFloat(componentData.maxValue)
    : maxValue && typeof maxValue === 'object' && maxValue.parsedFormula
    ? parseFloat(maxValue.parsedFormula)
    : parseFloat(maxValue) || null;

  // Set grid count based on field type
  let dynamicGridCount = gridCount;
  let horizontalGridCount = 7; // Y-axis grid should be consistent for both weekly and monthly

  if (isXAxisDate || isYAxisDate) {
    // X-axis (vertical grid) varies by period, Y-axis (horizontal grid) stays consistent
    dynamicGridCount = selectedPeriod === 'weekly' ? 7 : 30;
    horizontalGridCount = 7; // Keep Y-axis grid consistent
  } else if (
    xAxisBinding &&
    (xAxisBinding.source?.dataType === 'number' ||
      xAxisBinding.source?.type === 'number')
  ) {
    // Number fields: use maxValue from componentData or config
    if (resolvedMaxValue && resolvedMaxValue > 0) {
      // Use resolved maxValue - don't factor in height, use fixed calculation
      dynamicGridCount = Math.min(
        20,
        Math.max(5, Math.ceil(resolvedMaxValue / 20))
      );
    } else if (dataBinding && dataBinding.length > 0) {
      const fieldId = xAxisBinding.source?.fieldId;
      const values = dataBinding
        .map((item: any) => parseFloat(item[fieldId]) || 0)
        .filter((val: number) => !isNaN(val) && val > 0);

      if (values.length > 0) {
        const dataMaxValue = Math.max(...values);
        dynamicGridCount = Math.min(
          10,
          Math.max(5, Math.ceil((dataMaxValue + 20) / 10))
        );
      } else {
        dynamicGridCount = 5; // Default for blank number fields
      }
    } else {
      dynamicGridCount = 5; // Default when no data
    }
  } else if (
    yAxisBinding &&
    (yAxisBinding.source?.dataType === 'number' ||
      yAxisBinding.source?.type === 'number')
  ) {
    // Y-axis number fields: use maxValue from componentData or config
    if (resolvedMaxValue && resolvedMaxValue > 0) {
      // Use resolved maxValue - don't factor in height, use fixed calculation
      dynamicGridCount = Math.min(
        20,
        Math.max(5, Math.ceil(resolvedMaxValue / 20))
      );
    } else if (dataBinding && dataBinding.length > 0) {
      const fieldId = yAxisBinding.source?.fieldId;
      const values = dataBinding
        .map((item: any) => parseFloat(item[fieldId]) || 0)
        .filter((val: number) => !isNaN(val) && val > 0);

      if (values.length > 0) {
        const dataMaxValue = Math.max(...values);
        dynamicGridCount = Math.min(
          10,
          Math.max(5, Math.ceil((dataMaxValue + 20) / 10))
        );
      } else {
        dynamicGridCount = 5; // Default for blank number fields
      }
    } else {
      dynamicGridCount = 5; // Default when no data
    }
  }
  const search = queryString.parse(window?.location?.search);
  const isCanvas = !search?.target;

  // Convert grid line style to strokeDasharray
  const getStrokeDashArray = (style: string) => {
    switch (style) {
      case 'dashed':
        return '5,5';
      case 'dotted':
        return '2,2';
      default:
        return '';
    }
  };

  const chartConfig = useMemo(
    () => ({
      backgroundColor: backgroundColor || '#ffffff',
      backgroundGradientFrom: backgroundColor || '#ffffff',
      backgroundGradientTo: backgroundColor || '#ffffff',
      color: (opacity = 1) => textColor || `rgba(134, 65, 244, ${opacity})`,
      strokeWidth: 1,
      barPercentage: 0.5,
      useShadowColorFromDataset: false,
      decimalPlaces: 0,
      style: {
        borderRadius: 16,
      },
      propsForDots: {
        r: '6',
        strokeWidth: '1',
        stroke: textColor || '#8665F4',
      },
      propsForBackgroundLines: {
        strokeDasharray:
          chartType === 'line'
            ? getStrokeDashArray(gridLineStyle || 'solid')
            : '',
        stroke:
          chartType === 'line'
            ? showHorizontalGrid !== false
              ? gridLineColor || '#e3e3e3'
              : 'transparent'
            : gridLines?.enabled
            ? gridLines.color || '#e3e3e3'
            : 'transparent',
        strokeWidth: chartType === 'line' ? gridLineWidth || 1 : 1,
      },
      // Line Chart specific configuration - use datasetMode or auto-detect
      mode:
        props.datasetMode === 'multiple' ||
        props.datasetMode === 'multiplot' ||
        (props.datasets && props.datasets.length > 1) ||
        (props.dynamicFields &&
          props.dynamicFields.length > 0 &&
          props.datasetMode === 'multiplot')
          ? ('multiple' as const)
          : ('single' as const),
      showHorizontalGrid: showHorizontalGrid !== false,
      showVerticalGrid: showVerticalGrid || false,
      gridCount: dynamicGridCount, // X-axis grid count varies by period
      horizontalGridCount: horizontalGridCount, // Y-axis grid count consistent for legends
      multiAxis: {
        enabled: false,
      },
      legend: {
        enabled: showLegend !== false,
        position: legendPosition || 'bottom',
      },
      axisLabels: {
        x: {
          enabled: showXAxisLabel !== false,
          label: xAxisLabel || 'Time Period',
        },
        y: {
          enabled: showYAxisLabel !== false,
          label: yAxisLabel || 'Values',
        },
        yRight: {
          enabled: false,
          label: 'Secondary Values',
        },
      },
      // Multi Plot specific configuration
      multiPlot: {
        enabled: props.datasetMode === 'multiplot',
        xAxis: {
          type: xAxisType || 'field',
          fields: xAxisMultiFields || [],
          range: {
            min: xAxisRangeMin || 0,
            max: xAxisRangeMax || 100,
          },
          dateField: xAxisDateField,
          dateRange: {
            start: xAxisDateStart,
            end: xAxisDateEnd,
          },
        },
        yAxis: {
          type: yAxisType || 'field',
          fields: yAxisMultiFields || [],
          range: {
            min: yAxisRangeMin || 0,
            max: yAxisRangeMax || 100,
          },
          dateField: yAxisDateField,
          dateRange: {
            start: yAxisDateStart,
            end: yAxisDateEnd,
          },
        },
      },
    }),
    [
      backgroundColor,
      textColor,
      chartType,
      gridLineStyle,
      showHorizontalGrid,
      gridLineColor,
      gridLineWidth,
      gridLines,
      props.datasetMode,
      props.datasets,
      props.dynamicFields,
      showVerticalGrid,
      dynamicGridCount,
      horizontalGridCount,
      showLegend,
      legendPosition,
      showXAxisLabel,
      xAxisLabel,
      showYAxisLabel,
      yAxisLabel,
      xAxisType,
      xAxisMultiFields,
      xAxisRangeMin,
      xAxisRangeMax,
      xAxisDateField,
      xAxisDateStart,
      xAxisDateEnd,
      yAxisType,
      yAxisMultiFields,
      yAxisRangeMin,
      yAxisRangeMax,
      yAxisDateField,
      yAxisDateStart,
      yAxisDateEnd,
      selectedPeriod, // Add selectedPeriod as dependency
    ]
  );

  const filterControlsHeight = useMemo(() => {
    const shouldShowFilter =
      chartType === 'line' &&
      showDateFilter === true &&
      (isXAxisDate || isYAxisDate);
    return shouldShowFilter ? 48 : 0; // 32px button height + 16px padding
  }, [chartType, showDateFilter, isXAxisDate, isYAxisDate]);

  const legendHeight = useMemo(() => {
    if (showLegend === false) return 0;
    // Legend takes up space regardless of position
    return height < 300 ? 30 : 40;
  }, [showLegend, height]);

  const xAxisLabelHeight = useMemo(() => {
    if (showXAxisLabel === false) return 15; // Just for x-axis tick labels
    return height < 300 ? 35 : 45;
  }, [showXAxisLabel, height]);

  const commonProps = useMemo(
    () => ({
      width: width,
      height: height, // Use full height - let LineChart handle internal spacing
      chartConfig: chartConfig,
      bezier: chartType === 'line',
      hasFilterControls: filterControlsHeight > 0, // Pass filter info to chart
      style: {
        marginVertical: 8,
        borderRadius: 16,
      },
    }),
    [
      width,
      height,
      filterControlsHeight,
      legendHeight,
      xAxisLabelHeight,
      chartConfig,
      chartType,
    ]
  );

  // Filter controls component
  const FilterControls = useMemo(() => {
    const shouldShowFilter =
      chartType === 'line' &&
      showDateFilter === true &&
      (isXAxisDate || isYAxisDate);

    if (!shouldShowFilter) {
      return null;
    }

    const buttonHeight = 32;
    const controlsHeight = buttonHeight + 16;

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: 16,
          paddingVertical: 8,
          height: controlsHeight,
        }}
      >
        {/* Left Arrow */}
        <TouchableOpacity
          onPress={navigateLeft}
          style={{
            width: 32,
            height: buttonHeight,
            borderRadius: 6,
            backgroundColor: filterButtonInactiveColor || '#f0f0f0',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: filterButtonTextInactiveColor || '#666',
            }}
          >
            ←
          </Text>
        </TouchableOpacity>

        {/* Period Filter Buttons */}
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: filterButtonInactiveColor || '#f0f0f0',
            borderRadius: 6,
            padding: 2,
          }}
        >
          <TouchableOpacity
            onPress={() => togglePeriod('weekly')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 6,
              borderRadius: 4,
              backgroundColor:
                selectedPeriod === 'weekly'
                  ? filterButtonActiveColor || '#007AFF'
                  : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: selectedPeriod === 'weekly' ? '600' : '400',
                color:
                  selectedPeriod === 'weekly'
                    ? filterButtonTextActiveColor || '#fff'
                    : filterButtonTextInactiveColor || '#666',
              }}
            >
              Weekly
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => togglePeriod('monthly')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 6,
              borderRadius: 4,
              backgroundColor:
                selectedPeriod === 'monthly'
                  ? filterButtonActiveColor || '#007AFF'
                  : 'transparent',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: selectedPeriod === 'monthly' ? '600' : '400',
                color:
                  selectedPeriod === 'monthly'
                    ? filterButtonTextActiveColor || '#fff'
                    : filterButtonTextInactiveColor || '#666',
              }}
            >
              Monthly
            </Text>
          </TouchableOpacity>
        </View>

        {/* Right Arrow */}
        <TouchableOpacity
          onPress={navigateRight}
          style={{
            width: 32,
            height: buttonHeight,
            borderRadius: 6,
            backgroundColor: filterButtonInactiveColor || '#f0f0f0',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: filterButtonTextInactiveColor || '#666',
            }}
          >
            →
          </Text>
        </TouchableOpacity>
      </View>
    );
  }, [
    showDateFilter,
    isXAxisDate,
    isYAxisDate,
    selectedPeriod,
    filterButtonActiveColor,
    filterButtonInactiveColor,
    filterButtonTextActiveColor,
    filterButtonTextInactiveColor,
    navigateLeft,
    navigateRight,
    togglePeriod,
  ]);

  // Always call useStaticChartData hook - hooks must be called unconditionally
  const staticDataRaw: any = useStaticChartData({
    ...props,
    width,
    height,
    datasetMode:
      props.datasetMode ||
      ((props.datasets && props.datasets.length > 1) ||
      (props.dynamicFields && props.dynamicFields.length > 0)
        ? 'multiple'
        : 'single'),
    attributes: { ...attributes, maxValue },
    componentData,
    // Pass period filter and date offset
    periodFilter: selectedPeriod,
    dateOffset: dateOffset,
  });

  // Only use staticData if we're in canvas mode
  const staticData = isCanvas ? staticDataRaw : null;

  if (isCanvas) {
    const isZeroValues = allValuesZero(staticData);
    return (
      <View
        style={{
          opacity,
          backgroundColor: backgroundColor,
        }}
      >
        {chartType === 'bar' && (
          <BarChart
            data={staticData}
            {...commonProps}
            showGrid={gridLines?.enabled}
            showValuesOnTopOfBars={true}
            isZeroValues={isZeroValues}
          />
        )}
        {chartType === 'line' && (
          <View style={{ height: height }}>
            <LineChart
              data={staticData}
              {...commonProps}
              withDots={true}
              withShadow={false}
              withScrollableDot={false}
              isZeroValues={isZeroValues}
              maxValue={staticData.maxValue || resolvedMaxValue}
              periodFilter={selectedPeriod}
            >
              {FilterControls}
            </LineChart>
          </View>
        )}
        {chartType === 'pie' && (
          <PieChart
            data={
              staticData.datasets?.[0]?.data?.map(
                (value: number, index: number) => ({
                  name: staticData.labels?.[index] || `Item ${index + 1}`,
                  value: value,
                  color:
                    staticData.colors?.[index] ||
                    `hsl(${index * 60}, 70%, 50%)`,
                  legendFontColor: textColor || '#7F7F7F',
                  legendFontSize: 15,
                })
              ) || []
            }
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[width / 4, 0]}
            absolute={false}
            hasLegend={hasLegend?.enabled}
            isZeroValues={isZeroValues}
          />
        )}
        {chartType === 'radar' && (
          <RadarChart
            data={[]}
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={hasLegend?.enabled}
            isZeroValues={isZeroValues}
            datasets={staticData.datasets}
            labels={staticData.labels}
            value={0}
          />
        )}
      </View>
    );
  }

  const chartDataRaw: any = useMemo(
    () =>
      mapDataToChartFormat({
        ...props,
        width,
        height,
        datasetMode:
          props.datasetMode ||
          ((props.datasets && props.datasets.length > 1) ||
          (props.dynamicFields && props.dynamicFields.length > 0)
            ? 'multiple'
            : 'single'),
        databaseOptions: props.databaseOptions,
        attributes: { ...attributes, maxValue },
        componentData,
        periodFilter: selectedPeriod,
        dateOffset: dateOffset,
      }),
    [
      props,
      width,
      height,
      attributes,
      maxValue,
      componentData,
      selectedPeriod,
      dateOffset,
      dataBinding,
    ]
  );

  if (!isCanvas && dataBinding?.length) {
    const chartData = chartDataRaw;
    const isZeroValues = allValuesZero(chartData);
    return (
      <View style={{ opacity, backgroundColor }}>
        {FilterControls}
        {chartType === 'bar' && (
          <BarChart
            data={chartData}
            {...commonProps}
            showGrid={gridLines?.enabled}
            showValuesOnTopOfBars={true}
            onDataPointClick={() => {
              //   onPress?.(data);
            }}
            isZeroValues={isZeroValues}
          />
        )}
        {chartType === 'line' && (
          <View style={{ height: height }}>
            <LineChart
              data={chartData}
              {...commonProps}
              withDots={true}
              withShadow={false}
              withScrollableDot={false}
              isZeroValues={isZeroValues}
              maxValue={chartData.maxValue || resolvedMaxValue}
              periodFilter={selectedPeriod}
            />
          </View>
        )}
        {chartType === 'pie' && (
          <PieChart
            data={
              chartData.datasets?.[0]?.data?.map(
                (value: number, index: number) => ({
                  name: chartData.labels?.[index] || `Item ${index + 1}`,
                  value: value,
                  color:
                    chartData.colors?.[index] || `hsl(${index * 60}, 70%, 50%)`,
                  legendFontColor: textColor || '#7F7F7F',
                  legendFontSize: 15,
                })
              ) || []
            }
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[width / 4, 0]}
            absolute={false}
            hasLegend={hasLegend?.enabled}
            onDataPointClick={() => {
              // onPress?.(data);
            }}
            isZeroValues={isZeroValues}
          />
        )}
        {chartType === 'radar' && (
          <RadarChart
            data={[]}
            width={width}
            height={height}
            chartConfig={chartConfig}
            accessor="value"
            backgroundColor="transparent"
            paddingLeft="15"
            center={[0, 0]}
            absolute={false}
            hasLegend={hasLegend?.enabled}
            onDataPointClick={() => {
              // onPress?.(data);
            }}
            isZeroValues={isZeroValues}
            datasets={chartData.datasets}
            labels={chartData.labels}
          />
        )}
      </View>
    );
  }
  return <View />;
};

export default Charts;
